/**
 * 詳細画面が PJ モードに入るための検索条件。
 *
 * PJ 番号を画面の状態ではなく URL に置くのは、PJ サマリーから「この PJ が入った後の姿」へ
 * 直接送れるようにするためである。画面の状態に閉じると、遷移のたびに PJ を選び直すことになり、
 * PJ を先に選ぶという導線が成立しない。
 *
 * **鍵の名前は画面をまたいで同じ `pj` にする。** 画面ごとに違う名前を使うと、導線を
 * またいだ瞬間に PJ が外れて現在版へ落ちる。落ちたことは画面に出ないので、利用者は
 * 「PJ を適用した姿を見ている」と思ったままマスターを読む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 詳細画面が PJ モードに入るための検索条件を読み、パスに置かれた PJ と 1 つに畳む。
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 */
import { isPjMode } from '../lib/pj-mode.js';

import { PJ_PATH_PARAM } from './route-paths.js';

/** 詳細画面の検索条件。PJ モードのときだけ PJ 番号を持つ。 */
export interface PjSearch {
  /**
   * 重ねる PJ 番号。数字だけの番号は数値、それ以外（`AIRCLOSET-139162` など）は文字列。
   * 未指定ならマスターを読む。画面で使うときは `pjTextOf` で文字列へ戻す。
   */
  pj?: number | string;
}

/**
 * 検索条件から PJ 番号を読む。
 *
 * 空文字は指定なしと同じに畳む。手打ちの URL や古いブックマークから `?pj=` だけが
 * 残った状態で来たときに、空の PJ 番号で読取を撃たないためである。
 *
 * **数字だけの番号は数値のまま返す。** この戻り値は画面が読む値であると同時に、ルーターが
 * URL へ書き戻す値でもある。ルーターの既定の書き出しは「JSON として数値に読める文字列」を
 * 引用符で包むので、文字列で返すと `?pj=139162` が開いた瞬間に `?pj=%22139162%22` へ
 * 書き換わる（2026-09-25 の実測）。画面の側は `pjTextOf` で文字列へ戻して読む。
 *
 * **鍵は必ず自分の判定結果で塗り替える。** 検証器の戻り値は検証器を持たない親ルートの
 * 生の検索条件へ重ねられるので、鍵ごと落とすと生の値がそのまま画面へ届く。真偽値や配列が
 * 届くと読取の入力検証で弾かれ、画面は取得中のまま止まる。落としたつもりの値が、落ちずに
 * 画面を壊す。
 *
 * @param raw 生の検索条件
 * @returns PJ モードの検索条件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件から PJ 番号を読み、数字だけの番号は数値のまま、PJ として成立しない値は指定なしへ畳む。
 * @graph-connects readPjNumber [calls] 生の値から PJ 番号を読む
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 * @graph-connects searchValueOf [calls] 検索条件に置く形へ直す
 */
export function parsePjSearch(raw: Record<string, unknown>): PjSearch {
  const pj = readPjNumber(raw['pj']);
  return { pj: isPjMode(pj) ? searchValueOf(pj) : undefined };
}

/**
 * 番号（PJ 番号・開く処理の番号）を、検索条件に置く形へ直す。数字だけの番号は数値、それ以外は
 * 文字列のまま。
 *
 * **書き出しの規則をここ 1 箇所に置く。** ルーターの既定の書き出しは「JSON として数値に読める
 * 文字列」を引用符で包むので、数字だけの番号を文字列で持つと URL が `%22` つきに書き換わる。
 * `pj`（`parsePjSearch` / `pjLinkSearch`）と `op`（`flow-search.ts`）で同じ規則を書き写すと、
 * 片方だけ直したときに片方の鍵だけが引用符つきで残る。
 *
 * 数値にするのは、書き戻したときに綴りが変わらないものだけである。先頭が 0 の番号や桁の
 * 大きすぎる番号は、数値にすると `0123` → `123` のように別の番号になる。そうした綴りは
 * ルーターの既定の読み書きでも文字列のまま往復するので、文字列で残す。
 *
 * @param text 空でない番号の綴り
 * @returns 検索条件に置く番号
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件の番号を、URL へ書き戻しても綴りが変わらない形（数字だけなら数値）へ直す。
 * @graph-connects none
 */
export function searchValueOf(text: string): number | string {
  if (!/^\d+$/u.test(text)) return text;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && String(parsed) === text ? parsed : text;
}

/**
 * 検索条件の PJ 番号を、画面が読む文字列へ戻す。
 *
 * 検索条件には数字だけの番号が数値で入る（`parsePjSearch`）。画面の部品・読取のフック・
 * リンクは PJ 番号を文字列で受けるので、`useSearch` で読んだ直後にここを通す。
 *
 * @param pj 検索条件の PJ 番号。未指定なら undefined
 * @returns PJ 番号の文字列。未指定なら undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件の PJ 番号（数字だけなら数値）を、画面が読む文字列へ戻す。
 * @graph-connects none
 */
export function pjTextOf(pj: number | string | undefined): string | undefined {
  return pj === undefined ? undefined : String(pj);
}

/**
 * いま見ている PJ を、URL の 2 通りの置き場から 1 つの答えに畳む。
 *
 * **PJ は URL の 2 箇所に現れる。** 一覧・詳細では検索条件（`?pj=`）に、PJ サマリーと
 * PJ から見たフローではパスの親（`/pjs/$pjNumber/...`）に置かれる。置き場が 2 通り
 * あること自体は意図した設計で、後者は「PJ の無いこの画面は URL として作れない」ことを
 * 型で表すためにパスへ置いている。
 *
 * **だが読む側が片方しか見ないと、もう片方に居る間だけ PJ が消える。** 実際にそうなっていた —
 * 検索条件だけを読んでいたため、PJ モードの入口である PJ サマリーに立っている間はサイドバーも
 * 本文中のリンクも PJ を持たず、そこから業務ツリーへ移った瞬間にマスターへ落ちていた。PJ を
 * 選ぶ操作の起点で PJ が引き継がれない、という一番効く場所での取りこぼしである。
 *
 * **パスを検索条件より優先する。** パスに PJ があるルートは PJ が構造そのものなので、検索条件に
 * 別の PJ 番号が紛れ込んでいても、画面が実際に読んでいるのはパスの PJ である。優先順を逆にすると
 * 画面の中身と導線の PJ が食い違う。
 *
 * @param search 現在地の生の検索条件
 * @param params 現在地の生のパスパラメータ
 * @returns いま見ている PJ 番号。PJ を見ていなければ `undefined`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件とパスパラメータのどちらに置かれた PJ も、いま見ている PJ として読む。
 * @graph-connects readPjNumber [calls] 生の値から PJ 番号を読む
 * @graph-connects parsePjSearch [calls] 検索条件から PJ 番号を読む
 * @graph-connects pjTextOf [calls] 検索条件の PJ 番号を文字列へ戻す
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 */
export function currentPjOf(
  search: Record<string, unknown>,
  params: Record<string, unknown>,
): string | undefined {
  const fromPath = readPjNumber(params[PJ_PATH_PARAM]);
  if (isPjMode(fromPath)) return fromPath;
  return pjTextOf(parsePjSearch(search).pj);
}

/**
 * 生の検索条件 1 件を PJ 番号として読む。
 *
 * **数字だけの PJ 番号は数値として届く。** 検索条件は JSON として読まれるので、
 * `?pj=12345` は文字列ではなく数値になる。PJ 番号は数字だけでも成立する以上、
 * 数値を捨てると数字だけの PJ が「PJ を選んでいない」ことになり、その PJ だけ
 * どの画面でも差分が出ない。いったん文字列へ揃えて PJ として判定する（検索条件に置く形へは
 * `searchValueOf` が直す）。
 *
 * 数値と文字列以外（真偽値・配列・オブジェクト）は PJ 番号になりえないので落とす。
 *
 * @param value 生の検索条件の値
 * @returns PJ 番号。PJ として成立しなければ空文字
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 生の検索条件の値を PJ 番号として読み、成立しない値を空文字へ畳む。
 * @graph-connects none
 */
function readPjNumber(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}
